import { create } from "zustand"

export const useDictationStore = create((set, get) => ({
  // ── Panel teardown ──────────────────────────────────────────────────────
  // The recording panel registers a cleanup callback (clear timers, stop
  // WebSpeech, stop mic stream) on mount. closeDictation runs it before
  // wiping state so the ✕ close button, session reset, and route change
  // all release the same resources. Without this, setInterval timers and
  // the live mic stream survive panel close and cause the render storm /
  // freeze symptoms over a long session.
  _panelTeardown: null,
  setPanelTeardown: (fn) => set({ _panelTeardown: fn }),

  // UI state
  isOpen: false,
  conditionsPanelVisible: true,
  symptomsPanelVisible: false,
  historyPanelVisible: false,
  historyPanelMinimised: false,
  sideStackContainer: null,

  // Recording state
  isRecording: false,
  isPaused: false,
  status: "idle", // idle | recording | transcribing | summarising | done | soapReady | error
  timer: 0,

  // Content
  transcript: "",
  interimText: "",
  conditions: [],
  soap: null,
  assessmentFindings: [],
  appliedSections: {},

  // Open dictation — keeps conditions panel visible even after recording stops
  openDictation: () => set({ isOpen: true, conditionsPanelVisible: true, symptomsPanelVisible: false }),

  // Closes the panel AND releases all panel-owned resources (timers, mic,
  // WebSpeech, suggestion poll). Called by the ✕ button and by session reset.
  closeDictation: () => {
    const tearDown = get()._panelTeardown
    if (tearDown) {
      try { tearDown() } catch (e) { console.error("[DICTATION] teardown failed:", e) }
    }
    set({
      isOpen: false,
      isRecording: false,
      isPaused: false,
      status: "idle",
      timer: 0,
      transcript: "",
      interimText: "",
      conditions: [],
      soap: null,
      assessmentFindings: [],
      appliedSections: {},
      conditionsPanelVisible: true,
      symptomsPanelVisible: false,
    })
  },

  setIsRecording: (v) => set({ isRecording: v }),
  setIsPaused: (v) => set({ isPaused: v }),
  setStatus: (v) => set({ status: v }),
  setTimer: (fn) => set((state) => ({ timer: typeof fn === "function" ? fn(state.timer) : fn })),
  setTranscript: (v) => set({ transcript: v }),
  setInterimText: (v) => set({ interimText: v }),
  setConditions: (v) => set({ conditions: v }),
  setSoap: (v) => set({ soap: v }),
  setAppliedSection: (noteId) => set((state) => ({ appliedSections: { ...state.appliedSections, [noteId]: true } })),
  toggleConditionsPanel: () => set((state) => ({ conditionsPanelVisible: !state.conditionsPanelVisible })),
  showConditionsPanel: () => set({ conditionsPanelVisible: true }),
  toggleSymptomsPanel: () => set((state) => ({ symptomsPanelVisible: !state.symptomsPanelVisible })),
  showHistoryPanel: () => set({ historyPanelVisible: true }),
  hideHistoryPanel: () => set({ historyPanelVisible: false }),
  toggleHistoryPanel: () => set((state) => ({ historyPanelVisible: !state.historyPanelVisible })),
  minimiseHistoryPanel: () => set({ historyPanelMinimised: true }),
  expandHistoryPanel: () => set({ historyPanelMinimised: false }),
  toggleHistoryPanelMinimised: () => set((state) => ({ historyPanelMinimised: !state.historyPanelMinimised })),
  setSideStackContainer: (node) => set({ sideStackContainer: node }),

  addAssessmentFinding: (finding) => set((state) => ({
    assessmentFindings: [...state.assessmentFindings, finding]
  })),
  removeAssessmentFinding: (index) => set((state) => ({
    assessmentFindings: state.assessmentFindings.filter((_, i) => i !== index)
  })),
  updateAssessmentFinding: (index, updated) => set((state) => ({
    assessmentFindings: state.assessmentFindings.map((f, i) => i === index ? { ...f, ...updated } : f)
  })),

  // Hydrate dictation panel state from a draft session doc (Continue Draft flow).
  // Opens the panel, restores transcript + pending AI suggestions + applied tab map.
  // Status is set to "soapReady" if there's existing soap content, otherwise "idle"
  // so the user sees a "Resume Recording" button instead of "Start Recording".
  hydrateFromDraft: ({ transcript, pendingConditions, pendingSoap, pendingAppliedSections, assessmentFindings }) => {
    const hasContent = !!(transcript || pendingSoap || (pendingConditions && pendingConditions.length))
    set({
      isOpen: hasContent,
      conditionsPanelVisible: true,
      symptomsPanelVisible: false,
      isRecording: false,
      isPaused: false,
      status: pendingSoap ? "soapReady" : "idle",
      timer: 0,
      transcript: transcript || "",
      interimText: "",
      conditions: pendingConditions || [],
      soap: pendingSoap || null,
      assessmentFindings: assessmentFindings || [],
      appliedSections: pendingAppliedSections || {},
    })
  },
}))
